import { Box, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { navigate } from 'gatsby';
import React, { useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import AssignedUserDropdown from '@components/AssignedUserDropdown/AssignedUserDropdown';
import { UserStatusChip } from '@components/Chip';
import ProfileStatus from '@components/CurationBanner/PatientProfileStatus';
import MuiDataTable from '@components/DataTable/MuiDataTable/MuiDataTable';
import Icon from '@components/Icon/Icon';
import AddRecordModalContent from '@components/Modal/Content/AddRecordModalContent/AddRecordModalContent';
import ResultsSummary from '@components/ResultsSummary';
import RoundedButton from '@components/RoundedButton';
import SkeletonLoader from '@components/SkeletonLoader';

import { CLIENTS, PATIENTS } from '@constants/routes';

import { useGetAllUsers } from '@hooks/api/user';
import { useCuration } from '@hooks/useCuration';
import { useIsMobile } from '@hooks/useIsMobile';
import { useModal } from '@hooks/useModal';
import { useSession } from '@hooks/useSession';

import type { Client } from '@models/client';

import { capitalize, formatDate, formatName } from '@utils/format';

import { RoleName } from 'generated/types';

const messages = defineMessages({
  noData: {
    id: 'clientsMain.noClients',
    defaultMessage:
      'No {isPatient, select, true{Patients} other{Non-Patients}}',
  },
  addRecord: {
    id: 'clientsMain.addRecord',
    defaultMessage: 'Add Record',
  },
  parentAccount: {
    id: 'clientsMain.parentAccount',
    defaultMessage: 'Parent Account',
  },
  childAccount: {
    id: 'clientsMain.childAccount',
    defaultMessage: 'Child Account',
  },
  accountType: {
    id: 'clientsMain.accountType',
    defaultMessage: 'Account Type',
  },
  primaryAccountHolder: {
    id: 'clientsMain.primaryAccountHolder',
    defaultMessage: 'Primary Account Holder (Parent)',
  },
});

function ClientInformationList({
  data = [],
  type = '',
}: {
  data: Client[];
  type: string;
}) {
  const {
    state: { isCurator, isOrg, isPractitioner, isVitall },
  } = useSession();
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const { viewAsPatient } = useCuration();
  const { closeModal, openModal, setContent } = useModal();
  const isMobile = useIsMobile();

  const { data: usersData, loading: usersLoading } = useGetAllUsers();
  const loading = usersLoading;

  // Rows matching the currently active search/filter, computed by
  // mui-datatables (see options.onTableChange below). Resynced whenever the
  // underlying tab data changes (tab switch, refetch).
  const [filteredCount, setFilteredCount] = useState(data.length);
  useEffect(() => {
    setFilteredCount(data.length);
  }, [data]);

  const getAccountTypeFilterLabel = (value: string) =>
    (value as Client['accountRelationship']) === 'child'
      ? formatMessage(messages.childAccount)
      : formatMessage(messages.primaryAccountHolder);

  const userSortFn = (
    user_id_a: string,
    user_id_b: string,
    order: 'asc' | 'desc'
  ) => {
    const getUser = (id?: string) => {
      if (!id) {
        return undefined;
      }
      const user = usersData?.find(({ user_id }) => user_id === id);
      return formatName(user?.first_name, user?.last_name);
    };
    const valA = getUser(user_id_a);
    const valB = getUser(user_id_b);
    if (!valA) {
      return 1;
    }
    if (!valB) {
      return -1;
    }
    return valA.localeCompare(valB) * (order === 'asc' ? 1 : -1);
  };

  const handleOpenModal = (user_id: string) => {
    openModal({
      width: 500,
      props: {
        height: '300px',
      },
      children: null,
    });
    setContent({
      children: (
        <AddRecordModalContent user_id={user_id} onClose={closeModal} />
      ),
    });
  };

  return (
    <SkeletonLoader loading={loading} width="100%" sx={{ height: '80%' }}>
      <ResultsSummary
        label={type.toLowerCase()}
        shown={filteredCount}
        total={data.length}
      />
      <MuiDataTable
        columns={[
          {
            name: 'vitall_id',
            className: 'small',
            label: 'ID',
            options: {
              searchable: true,
              sort: true,
              display: isVitall,
            },
          },
          {
            name: 'name',
            className: 'blue-hover',
            label: `${isOrg ? 'Patient' : 'Client'} Name`,
            options: {
              searchable: true,
              sort: true,
              ...(isVitall && {
                customBodyRender: (
                  value: Client['name'],
                  { rowData }: { rowData: unknown[] }
                ) => {
                  // accountRelationship is the last column; use rowData (not
                  // data[rowIndex]) so sorted/filtered rows keep the correct icon.
                  const accountRelationship = rowData.at(
                    -1
                  ) as Client['accountRelationship'];
                  const isChildAccount = accountRelationship === 'child';
                  return (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <Tooltip
                        title={formatMessage(
                          isChildAccount
                            ? messages.childAccount
                            : messages.parentAccount
                        )}
                        disableInteractive
                      >
                        <Box sx={{ display: 'flex' }}>
                          <Icon
                            name={isChildAccount ? 'user' : 'users'}
                            type={isChildAccount ? 'far' : 'fas'}
                            size="sm"
                            color={theme.palette.primary.main}
                          />
                        </Box>
                      </Tooltip>
                      {value}
                    </Box>
                  );
                },
              }),
            },
          },
          {
            name: 'lastLogin',
            className: 'date',
            label: 'Last Login Date',
            options: {
              customHeadLabelRender: () => 'Last Login Date',
              customBodyRender: (value: Client['lastLogin']) =>
                value
                  ? formatDate({ date: value, formatter: 'datetime' })
                  : '-',
              filter: true,
              sort: true,
            },
          },
          {
            name: 'parent_account',
            className: 'small',
            label: `Org Name`,
            options: {
              display: isVitall,
              customBodyRender: (value: Client['parent_account']) =>
                value?.account_name,
              searchable: true,
              sort: true,
              filter: true,
            },
          },
          {
            name: 'plan',
            className: 'small',
            label: `Plan Type`,
            options: {
              display: type !== 'NON PATIENTS' && isVitall,
              customBodyRender: (value: Client['plan']) => value?.name,
              searchable: true,
              sort: true,
              filter: true,
              ...(isCurator || isPractitioner
                ? {
                    filterOptions: {
                      names: [
                        ...new Set(
                          data
                            .map((c) => c.plan?.name)
                            .filter((name): name is string => Boolean(name))
                        ),
                        ...(data.some((c) => !c.plan?.name) ? ['No Plan'] : []),
                      ],
                      logic: (prop, filters: string[]) => {
                        if (!filters.length) return false;
                        const plan = prop as unknown as Client['plan'];
                        const name = plan?.name || 'No Plan';
                        return !filters.includes(name);
                      },
                    },
                  }
                : {}),
            },
          },
          {
            name: 'curator',
            label: 'Curator',
            options: {
              customBodyRender: (value: Client['curator']) => {
                return (
                  <AssignedUserDropdown
                    name={formatName(value?.first_name, value?.last_name)}
                    role={RoleName.Curator}
                    vitall_patient_id={value?.vitall_patient_id}
                    value={value?.curator_user_id}
                  />
                );
              },
              display: type !== 'NON PATIENTS' && isVitall,
              filter: type !== 'NON PATIENTS' && isVitall,
              filterType: 'multiselect',
              filterOptions: {
                names: [
                  ...new Set(
                    data
                      .map((c) =>
                        formatName(c.curator?.first_name, c.curator?.last_name)
                      )
                      .filter((name): name is string => Boolean(name))
                  ),
                  ...(data.some(
                    (c) =>
                      !formatName(c.curator?.first_name, c.curator?.last_name)
                  )
                    ? ['Unassigned']
                    : []),
                ],
                logic: (prop, filters: string[]) => {
                  if (!filters.length) return false;
                  const curator = prop as unknown as Client['curator'];
                  const name =
                    formatName(curator?.first_name, curator?.last_name) ||
                    'Unassigned';
                  return !filters.includes(name);
                },
              },
              sort: true,
              sortCompare: (order) => {
                return (patientA, patientB) => {
                  return userSortFn(
                    patientA.data.curator_user_id,
                    patientB.data.curator_user_id,
                    order
                  );
                };
              },
            },
          },
          {
            name: 'practitioner',
            label: 'Practitioner',
            options: {
              customBodyRender: (
                value: Client['practitioner'],
                { rowData }
              ) => {
                // Parent account is the 4th column (3rd index)
                const parent_account_id = (
                  rowData.at(3) as Client['parent_account']
                )?.account_id;

                if (!parent_account_id && isVitall) {
                  return '-';
                }
                return (
                  <AssignedUserDropdown
                    account_id={parent_account_id}
                    name={formatName(value?.first_name, value?.last_name)}
                    vitall_patient_id={value?.vitall_patient_id}
                    value={value?.practitioner_user_id}
                    role={RoleName.Practitioner}
                  />
                );
              },
              display: type !== 'NON PATIENTS',
              filter: type !== 'NON PATIENTS',
              filterType: 'multiselect',
              filterOptions: {
                names: [
                  ...new Set(
                    data
                      .map((c) =>
                        formatName(
                          c.practitioner?.first_name,
                          c.practitioner?.last_name
                        )
                      )
                      .filter((name): name is string => Boolean(name))
                  ),
                  ...(data.some(
                    (c) =>
                      !formatName(
                        c.practitioner?.first_name,
                        c.practitioner?.last_name
                      )
                  )
                    ? ['Unassigned']
                    : []),
                ],
                // mui-datatables types this as string but passes the raw object
                logic: (prop, filters: string[]) => {
                  if (!filters.length) return false;
                  const practitioner =
                    prop as unknown as Client['practitioner'];
                  const name =
                    formatName(
                      practitioner?.first_name,
                      practitioner?.last_name
                    ) || 'Unassigned';
                  return !filters.includes(name);
                },
              },
              sort: true,
              sortCompare: (order) => {
                return (patientA, patientB) => {
                  return userSortFn(
                    patientA.data.practitioner_user_id,
                    patientB.data.practitioner_user_id,
                    order
                  );
                };
              },
            },
          },
          {
            name: 'userStatus',
            label: 'User Status',
            options: {
              customBodyRender: (value: Client['userStatus']) => (
                <UserStatusChip status={value} />
              ),
              filter: true,
              filterType: 'multiselect',
              filterOptions: {
                renderValue: (value) => capitalize(value),
              },
            },
          },
          {
            name: 'patient',
            label: 'Profile Status',
            options: {
              customBodyRender: (value: Client['patient']) => (
                <ProfileStatus
                  read_only={isOrg}
                  status={value?.status}
                  variant="secondary"
                  vitall_patient_id={value?.vitall_patient_id}
                />
              ),
              display: type !== 'NON PATIENTS' && isVitall,
              sort: true,
              sortCompare: (order) => {
                return (patientA, patientB) => {
                  const valA = patientA.data.status.sequence;
                  const valB = patientB.data.status.sequence;
                  return (valA - valB) * (order === 'asc' ? 1 : -1);
                };
              },
            },
          },
          {
            name: 'newRecords',
            label: 'New Records',
            options: {
              customBodyRender: (value: Client['newRecords']) => value ?? 0,
              sort: true,
              display: !(isMobile || type === 'NON PATIENTS') && isVitall,
            },
          },
          {
            name: 'reviewRecords',
            label: 'Records Needing Review',
            options: {
              customBodyRender: (value: Client['reviewRecords']) => value ?? 0,
              sort: true,
              display: !(isMobile || type === 'NON PATIENTS') && isVitall,
            },
          },
          {
            name: 'draftRecords',
            label: 'Draft Records',
            options: {
              customBodyRender: (value: Client['draftRecords']) => value ?? 0,
              sort: true,
              display: !(isMobile || type === 'NON PATIENTS') && isVitall,
            },
          },
          {
            name: 'publishedRecords',
            label: 'Published Records',
            options: {
              customBodyRender: (value: Client['publishedRecords']) =>
                value ?? 0,
              sort: true,
              display: !(isMobile || type === 'NON PATIENTS') && isVitall,
            },
          },
          {
            name: 'group_name',
            label: 'Group',
            options: {
              filter: isVitall,
              display: isVitall,
            },
          },
          {
            name: 'user_id',
            options: {
              customHeadLabelRender: () => null,
              customBodyRender: (value: Client['user_id']) => (
                <RoundedButton
                  disabled={Boolean(!value)}
                  // eslint-disable-next-line @typescript-eslint/no-misused-promises
                  onClick={async (e) => {
                    e.stopPropagation();
                    handleOpenModal(value ?? '');
                  }}
                >
                  {formatMessage(messages.addRecord)}
                </RoundedButton>
              ),
              display: type !== 'NON PATIENTS' && isVitall,
            },
          },
          {
            name: 'accountRelationship',
            label: formatMessage(messages.accountType),
            options: {
              display: false,
              viewColumns: false,
              filter: isCurator,
              filterType: 'multiselect',
              filterOptions: {
                renderValue: getAccountTypeFilterLabel,
              },
              customFilterListOptions: {
                render: getAccountTypeFilterLabel,
              },
            },
          },
        ]}
        data={data}
        pageParam="clients"
        options={{
          sortOrder: {
            name: 'name',
            direction: 'asc',
          },
          sort: true,
          search: true,
          filter: true,
          textLabels: {
            body: {
              noMatch: formatMessage(messages.noData, {
                isPatient: Boolean(type === 'PATIENTS'),
              }),
            },
          },
          onTableChange: (_action, tableState) => {
            setFilteredCount(tableState.displayData.length);
          },
          onRowClick: async (_rowData, { dataIndex }) => {
            if (dataIndex <= data.length - 1) {
              const row = data[dataIndex];
              const { user_id } = row;
              if (user_id) {
                await viewAsPatient(user_id);
                if (isVitall) {
                  navigate(`${CLIENTS}${user_id}`);
                } else if (isOrg) {
                  navigate(`${PATIENTS}${user_id}`);
                }
              }
            }
          },
        }}
      />
    </SkeletonLoader>
  );
}

export default ClientInformationList;
